import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api/client";
import { useBootstrap } from "../hooks/queries";
import { queryClient } from "../hooks/queries";
import { requestGoogleIdToken } from "./googleGis";
import type { AuthMode, AuthPublicConfig, StudioUser } from "./types";
import { setStudioAuthUser } from "./studioAuthBridge";

type AuthContextValue = {
  user: StudioUser | null;
  auth: AuthPublicConfig;
  ready: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  requiresSignIn: boolean;
};

const defaultAuth: AuthPublicConfig = {
  mode: "off",
  google_client_id: "",
  google_enabled: false,
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const boot = useBootstrap();
  const [user, setUser] = useState<StudioUser | null>(null);
  const [auth, setAuth] = useState<AuthPublicConfig>(defaultAuth);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (boot.isPending) return;
    if (boot.data) {
      const nextAuth = (boot.data.auth as AuthPublicConfig | undefined) || defaultAuth;
      const nextUser = (boot.data.user as StudioUser | null | undefined) || null;
      setAuth(nextAuth);
      setUser(nextUser);
      setStudioAuthUser(nextUser);
    } else {
      setAuth(defaultAuth);
      setUser(null);
      setStudioAuthUser(null);
    }
    setReady(true);
  }, [boot.isPending, boot.data, boot.error]);

  const signIn = useCallback(async () => {
    const clientId =
      auth.google_client_id ||
      String(import.meta.env.VITE_GOOGLE_CLIENT_ID || "").trim();
    if (!auth.google_enabled && !clientId) {
      throw new Error("Google sign-in is not configured");
    }
    const idToken = await requestGoogleIdToken(
      auth.google_client_id || clientId,
    );
    const result = await api<{ user: StudioUser }>("/api/auth/google", {
      method: "POST",
      json: { id_token: idToken },
    });
    setUser(result.user);
    setStudioAuthUser(result.user);
    await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    await queryClient.invalidateQueries({ queryKey: ["productions"] });
    await queryClient.invalidateQueries({ queryKey: ["cast-assets"] });
  }, [auth.google_client_id, auth.google_enabled]);

  const signOut = useCallback(async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } finally {
      setUser(null);
      setStudioAuthUser(null);
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      await queryClient.invalidateQueries({ queryKey: ["productions"] });
      await queryClient.invalidateQueries({ queryKey: ["cast-assets"] });
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const mode: AuthMode = auth.mode || "off";
    return {
      user,
      auth,
      ready,
      signIn,
      signOut,
      requiresSignIn: mode === "required" && !user,
    };
  }, [user, auth, ready, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
